import React, { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import { useTable } from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { permissionColumnGroups } from './masterList';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Type options
const typeOptions = [
  { value: "1", label: "Read" },
  { value: "2", label: "Write" },
  { value: "3", label: "Edit" },
  { value: "4", label: "Delete" },
];

const ModulePermissionReport = () => {
  const [selectedType, setSelectedType] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedSewadar, setSelectedSewadar] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sewadars, setSewadars] = useState([]);
  const token = localStorage.getItem("token");

  const moduleLabelMap = {};
  Object.keys(permissionColumnGroups).forEach(group => {
    Object.keys(permissionColumnGroups[group].items).forEach(col => {
      moduleLabelMap[col] = permissionColumnGroups[group].items[col];
    });
  });

  const moduleOptions = Object.keys(moduleLabelMap).map(col => ({
    value: col,
    label: moduleLabelMap[col],
  }));

  const fetchSewadars = async () => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getSewadar`, {
        params: { code: "6406" },
        headers: { Authorization: `Bearer ${token}` },
      });
      const options = response.data.map(s => ({
        value: s.id,
        label: `${s.firstName} ${s.lastName}`,
      }));
      setSewadars(options);
    } catch (err) {
      console.error("Error fetching Sewadars:", err);
    }
  };

  useEffect(() => {
    fetchSewadars();
  }, []);

  useEffect(() => {
    setPermissions([]);
  }, [selectedType, selectedModule, selectedSewadar]);

  const fetchTypeWisePermissions = async () => {
    if (!selectedType) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getTypeWisePermissions`, {
        params: { type: selectedType.value },
        headers: { Authorization: `Bearer ${token}` },
      });

      const reportWithLabels = response.data.map(row => ({
        module: moduleLabelMap[row.module] || row.module,
        sewadars: row.sewadars.split(",").map(name => name.trim()),
      }));

      setPermissions(reportWithLabels);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleWisePermissions = async () => {
    if (!selectedModule) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getModuleWisePermissions`, {
        params: { module: selectedModule.value },
        headers: { Authorization: `Bearer ${token}` },
      });

      const mapped = response.data.map(row => ({
        sewadar: row.sewadar,
        Read: !!row.Read,
        Write: !!row.Write,
        Edit: !!row.Edit,
        Delete: !!row.Delete,
      }));

      setPermissions(mapped);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Sewadar-wise permissions
  const fetchSewadarWisePermissions = async () => {
    if (!selectedSewadar) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getSewadarWisePermissions`, {
        params: { sewadarId: selectedSewadar.value },
        headers: { Authorization: `Bearer ${token}` },
      });

      const mapped = response.data.data.map(row => ({
        module: moduleLabelMap[row.module] || row.module,
        Read: !!row.value?.read,
        Write: !!row.value?.write,
        Edit: !!row.value?.edit,
        Delete: !!row.value?.delete,
      }));

      setPermissions(mapped);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    if (!permissions.length) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Module Permissions");

    // Set some default column widths
    worksheet.columns = [
      { header: "Module", key: "module", width: 30 },
      { header: "Sewadar", key: "sewadar", width: 40 },
      { header: "Read", key: "Read", width: 10 },
      { header: "Write", key: "Write", width: 10 },
      { header: "Edit", key: "Edit", width: 10 },
      { header: "Delete", key: "Delete", width: 10 },
      { header: "Count", key: "count", width: 10 },
    ];

    // Add top label info
    let topInfo = "Module Permission Report: ";
    if (selectedType) topInfo += `Type - ${selectedType.label}`;
    else if (selectedModule) topInfo += `Module - ${selectedModule.label}`;
    else if (selectedSewadar) topInfo += `Sewadar - ${selectedSewadar.label}`;

    worksheet.mergeCells("A1:F1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = topInfo;
    titleCell.font = { size: 14, bold: true };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };

    worksheet.addRow([]);

    // Prepare data
    if (selectedType) {
      permissions.forEach(p => {
        worksheet.addRow({
          module: p.module,
          sewadar: p.sewadars.join(", "),
          count: p.sewadars.length,
        });
      });
    } else {
      permissions.forEach(p => {
        worksheet.addRow({
          module: p.module || "",
          sewadar: p.sewadar || "",
          Read: p.Read ? "Yes" : "No",
          Write: p.Write ? "Yes" : "No",
          Edit: p.Edit ? "Yes" : "No",
          Delete: p.Delete ? "Yes" : "No",
        });
      });
    }

    // Apply styling to header row
    const headerRow = worksheet.getRow(3); // Headers are on 3rd row after topInfo + empty row
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF007BFF" } };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };

    // Apply borders
    worksheet.eachRow({ includeEmpty: false }, row => {
      row.eachCell({ includeEmpty: false }, cell => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Export
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "ModulePermissions.xlsx");
  };
  // Filter
  const filteredPermissions = permissions.filter(p =>
    Object.values(p).some(val => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const sortedPermissions = useMemo(() => {
    if (!filteredPermissions.length) return [];
  
    // TYPE WISE → higher count first
    if (selectedType) {
      return [...filteredPermissions].sort(
        (a, b) => b.sewadars.length - a.sewadars.length
      );
    }
  
    // MODULE / SEWADAR WISE → rows having permissions first
    return [...filteredPermissions].sort((a, b) => {
      const score = row =>
        (row.Read ? 1 : 0) +
        (row.Write ? 1 : 0) +
        (row.Edit ? 1 : 0) +
        (row.Delete ? 1 : 0);
  
      return score(b) - score(a);
    });
  }, [filteredPermissions, selectedType]);
  

  // Columns setup
  const columns = useMemo(() => {
    const renderIcon = value =>
      value ? (
        <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#198754" }} />
      ) : (
        "-" 
      );

    if (selectedType) {
      return [
        { Header: "Module", accessor: "module" },
        {
          Header: "Sewadars",
          accessor: "sewadars",
          Cell: ({ value }) => (
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {value.map((name, idx) => (
                <span
                  key={idx}
                  style={{
                    width: "23%",
                    marginBottom: "5px",
                    position: "relative",
                    paddingLeft: "18px",
                  }}
                >
                  <span style={{ position: "absolute", left: 0, color: "#007bff" }}>•</span>
                  {name}
                </span>
              ))}
            </div>
          ),
        },
        {
          Header: "Count",
          accessor: "sewadarsCount",
          Cell: ({ row }) => row.original.sewadars.length,
        },
      ];
    } else if (selectedModule || selectedSewadar) {
      return [
        { Header: selectedModule ? "Sewadar" : "Module", accessor: selectedModule ? "sewadar" : "module" },
        { Header: "Read", accessor: "Read", Cell: ({ value }) => renderIcon(value) },
        { Header: "Write", accessor: "Write", Cell: ({ value }) => renderIcon(value) },
        { Header: "Edit", accessor: "Edit", Cell: ({ value }) => renderIcon(value) },
        { Header: "Delete", accessor: "Delete", Cell: ({ value }) => renderIcon(value) },
      ];
    }
    return [];
  }, [selectedType, selectedModule, selectedSewadar]);

  const tableInstance = useTable({ columns, data: sortedPermissions });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const handleGetClick = () => {
    if (selectedType) fetchTypeWisePermissions();
    else if (selectedModule) fetchModuleWisePermissions();
    else if (selectedSewadar) fetchSewadarWisePermissions();
  };

  return (
    <div className="formcontainer mt-5">
      <h3>Module Permission Report</h3>

      <div className="row mb-3">
        <div className="col-md-3">
          <label>Type</label>
          <Select
            options={typeOptions}
            value={selectedType}
            onChange={val => {
              setSelectedType(val);
              setSelectedModule(null);
              setSelectedSewadar(null);
            }}
            isDisabled={!!selectedModule || !!selectedSewadar}
            isClearable
          />
        </div>
        <div className="col-md-3">
          <label>Module</label>
          <Select
            options={moduleOptions}
            value={selectedModule}
            onChange={val => {
              setSelectedModule(val);
              setSelectedType(null);
              setSelectedSewadar(null);
            }}
            isDisabled={!!selectedType || !!selectedSewadar}
            isClearable
          />
        </div>
        <div className="col-md-3">
          <label>Sewadar</label>
          <Select
            options={sewadars}
            value={selectedSewadar}
            onChange={val => {
              setSelectedSewadar(val);
              setSelectedType(null);
              setSelectedModule(null);
            }}
            isDisabled={!!selectedType || !!selectedModule}
            isClearable
          />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-primary w-100" onClick={handleGetClick}>
            Get
          </button>
        </div>
        {/* <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-success w-100" onClick={exportToExcel}>
            Export to Excel
          </button>
        </div> */}
      </div>

      <div className="row mb-3">
        <div className="col-md-12">
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading && <p>Loading...</p>}

      {!loading && permissions.length > 0 && filteredPermissions.length > 0 && (
        <table {...getTableProps()} className="table table-bordered table-striped mt-3 align-middle">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!loading && permissions.length > 0 && filteredPermissions.length === 0 && <p>No matching records found.</p>}
      {!loading && permissions.length === 0 && <p>No permissions found.</p>}
    </div>
  );
};

export default ModulePermissionReport;
